import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Image from "../components/elements/Image";
import Logo from "../../src/assets/images/logo.png";
import { classNames } from "../helpers/classNames";
import Title from "../components/elements/Title";
import { lngDetect } from "../components/common/lngDetect";
import { postFrontDetails } from "../services/postService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

const FrontFooter = () => {
  const { data: communityData } = useQuery({
    queryKey: ["front-contact-section"],
    queryFn: async () => {
      const res = await postFrontDetails({
        slug: "contact-section",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
  });

  const newCommunityData = {
    title: lngDetect(communityData?.content)?.excerpt,
    description: lngDetect(communityData?.content)?.content,
  };

  // Animation Variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  // Data
  const appStoreData = [
    {
      name: "Google Play",
      link: "/",
      icon: "fa-brands fa-google-play",
    },
    {
      name: "Apple Store",
      link: "/",
      icon: "fa-brands fa-apple text-2xl",
    },
  ];

  const socialLinks = [
    {
      name: "Facebook",
      icon: "fa-brands fa-facebook",
      link: "#",
    },
    {
      name: "Instagram",
      icon: "fa-brands fa-instagram",
      link: "#",
    },
    {
      name: "Twitter",
      icon: "fa-brands fa-twitter",
      link: "#",
    },
    {
      name: "TikTok",
      icon: "fa-brands fa-tiktok",
      link: "#",
    },
  ];

  const footerLinks = [
    {
      name: "Terms & Conditions",
      link: "/terms",
    },
  ];

  return (
    <footer className="relative bg-woodsmoke overflow-hidden z-0">
      {/* Background Gradients */}
      <motion.div
        animate={{ opacity: [0.1, 0.2, 0.1], scale: [1, 1.2, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-0 left-0 w-[600px] h-[600px] bg-electric-violet/30 rounded-full blur-[120px] z-[-1]"
      />

      {/* <motion.div
        animate={{ opacity: [0.1, 0.2, 0.1], scale: [1, 1.2, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-yellow-400/20 rounded-full blur-[120px] z-[-1]"
      /> */}

      {/* Main Container */}
      <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl mx-auto px-4">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* CTA Section */}
          <div className="relative flex items-center justify-center py-10 sm:py-20">
            <div className="w-full max-w-3xl mx-auto text-center space-y-10">
              <motion.div
                className="flex items-center justify-center"
                variants={item}
              >
                <Title title={newCommunityData?.title} />
              </motion.div>

              {/* Title */}
              <motion.div
                className="mx-auto w-full max-w-2xl xl:max-w-3xl text-center"
                variants={item}
              >
                <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold leading-normal">
                  {newCommunityData?.description}
                </h2>
              </motion.div>

              {/* App Store Buttons */}
              {appStoreData?.length > 0 && (
                <motion.div
                  variants={item}
                  className="flex flex-wrap items-center justify-center gap-4"
                >
                  {appStoreData.map((store, index) => (
                    <motion.a
                      key={index}
                      href={store.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-6 h-12 rounded-full font-bold text-base transition-all flex items-center justify-center gap-3 shadow-xl bg-gradient-to-r from-yellow-400 to-orange-500 text-[#320051] hover:from-yellow-300 hover:to-orange-400"
                    >
                      <i className={classNames("fa-fw", store.icon)} />
                      <span>{store.name}</span>
                    </motion.a>
                  ))}
                </motion.div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10" />

          {/* Footer Bottom */}
          <div className="py-12">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-10">
              {/* Logo */}
              <motion.div
                variants={item}
                className="relative aspect-[3/1] w-32 sm:w-96"
              >
                <Link
                  to="/"
                  className="w-full h-full flex items-center justify-center"
                >
                  <Image src={Logo} effect="blur" classes="object-contain" />
                </Link>
              </motion.div>

              <div className="space-y-4">
                {/* Navigation Links */}
                <motion.nav
                  variants={item}
                  className="flex flex-wrap items-center justify-center gap-6 sm:gap-8"
                >
                  {footerLinks.map((link, index) => (
                    <Link
                      key={index}
                      to={link.link}
                      className="text-sm sm:text-base text-white/60 hover:text-white transition-colors duration-300"
                    >
                      {link.name}
                    </Link>
                  ))}
                </motion.nav>

                {/* Social Media */}
                <motion.div
                  variants={item}
                  className="flex items-center justify-end gap-4"
                >
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={social.name}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-electric-violet transition-all duration-300"
                    >
                      <i className={classNames("fa-fw", social.icon)} />
                    </motion.a>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
          {/* Copyright */}
          <motion.div variants={item} className="py-4">
            <p className="text-sm sm:text-base text-white/50 text-center ">
              © {new Date().getFullYear()}{" "}
              <span className="text-white font-semibold">Biggest Fan</span>. All
              rights reserved.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

export default FrontFooter;
